import { useState } from "react";
import { useCreate, useGetList, useNotify } from "ra-core";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import type { Contact, FeatureCohort } from "../types";
import { normalizePhoneDigits } from "./normalizePhone";

const ENVIRONMENTS = [
  { id: "all", label: "All environments" },
  { id: "staging", label: "Staging only" },
  { id: "prod", label: "Prod only" },
] as const;

type PendingMember = {
  digits: string;
  contactId?: string;
  label?: string;
};

/**
 * Add a member to a feature cohort — either picked from the contacts list
 * (zero-ambiguity: in the deployed CRM these are the bot's WhatsApp contacts,
 * whose record already carries the wa_id) or as an arbitrary phone number
 * (strictly normalized; numbers without a country code are rejected, never
 * guessed).
 */
export const AddMemberDialog = ({
  cohort,
  open,
  onOpenChange,
  onCreated,
}: {
  cohort: FeatureCohort;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) => {
  const notify = useNotify();
  const [create, { isPending: isSaving }] = useCreate();
  const [tab, setTab] = useState<string>("contacts");
  const [search, setSearch] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [pending, setPending] = useState<PendingMember | null>(null);
  const [environment, setEnvironment] =
    useState<(typeof ENVIRONMENTS)[number]["id"]>("all");
  const [note, setNote] = useState("");

  const {
    data: contacts,
    error: contactsError,
    isPending: contactsPending,
  } = useGetList<Contact>(
    "contacts",
    {
      filter: search ? { q: search } : {},
      pagination: { page: 1, perPage: 10 },
      sort: { field: "last_seen", order: "DESC" },
    },
    { enabled: open && tab === "contacts", retry: false },
  );

  const reset = () => {
    setSearch("");
    setPhoneInput("");
    setPending(null);
    setEnvironment("all");
    setNote("");
    setTab("contacts");
  };

  const contactPhone = (contact: Contact): string | null => {
    const raw =
      contact.phone_jsonb?.find((phone) => phone?.number)?.number ??
      String(contact.id ?? "");
    const normalized = normalizePhoneDigits(raw);
    return normalized.ok ? normalized.digits : null;
  };

  const contactLabel = (contact: Contact): string =>
    [contact.first_name, contact.last_name].filter(Boolean).join(" ").trim() ||
    String(contact.id);

  const pickContact = (contact: Contact) => {
    const digits = contactPhone(contact);
    if (!digits) {
      notify(
        "This contact has no usable international phone number — add them via the Phone number tab.",
        { type: "warning" },
      );
      return;
    }
    const label = contactLabel(contact);
    setPending({ digits, contactId: String(contact.id), label });
    if (!note) {
      setNote(label);
    }
  };

  const phonePreview = phoneInput ? normalizePhoneDigits(phoneInput) : null;

  const applyPhoneInput = () => {
    if (phonePreview?.ok) {
      setPending({ digits: phonePreview.digits });
    }
  };

  const save = () => {
    if (!pending) {
      return;
    }
    create(
      "feature_cohort_members",
      {
        data: {
          cohort_slug: cohort.slug,
          phone_digits: pending.digits,
          mode: "grant",
          environment,
          source: "crm",
          contact_id: pending.contactId ?? null,
          note: note.trim() || null,
        },
      },
      {
        onSuccess: () => {
          notify(
            `Added +${pending.digits} to ${cohort.name} — live in the bot within a minute.`,
            { type: "info" },
          );
          onCreated();
          onOpenChange(false);
          reset();
        },
        onError: (error: unknown) => {
          const message = String(
            (error as { message?: string; code?: string })?.message ?? error,
          );
          const code = (error as { code?: string })?.code;
          if (message.includes("duplicate") || code === "23505") {
            notify(
              `+${pending.digits} is already a member of ${cohort.name}.`,
              {
                type: "warning",
              },
            );
          } else {
            notify(
              "Could not add the member — check that the feature_cohorts migration is applied.",
              { type: "warning" },
            );
          }
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          reset();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add member to {cohort.name}</DialogTitle>
          <DialogDescription>
            Pick a WhatsApp contact, or enter any international phone number.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="contacts">From contacts</TabsTrigger>
            <TabsTrigger value="phone">Phone number</TabsTrigger>
          </TabsList>

          <TabsContent value="contacts" className="flex flex-col gap-2">
            <Input
              placeholder="Search contacts…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {contactsError ? (
              <p className="text-sm text-muted-foreground">
                Couldn&apos;t load contacts here — use the Phone number tab
                instead.
              </p>
            ) : contactsPending ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <ul className="max-h-48 overflow-y-auto">
                {(contacts ?? []).map((contact) => {
                  const digits = contactPhone(contact);
                  const isPicked = !!digits && pending?.digits === digits;
                  return (
                    <li key={contact.id}>
                      <button
                        type="button"
                        onClick={() => pickContact(contact)}
                        className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-accent ${
                          isPicked ? "bg-accent" : ""
                        }`}
                      >
                        <span className="truncate">
                          {contactLabel(contact)}
                        </span>
                        <span className="ml-2 font-mono text-xs text-muted-foreground">
                          {digits ? `+${digits}` : "no number"}
                        </span>
                      </button>
                    </li>
                  );
                })}
                {(contacts ?? []).length === 0 && (
                  <li className="px-2 py-1.5 text-sm text-muted-foreground">
                    No contacts found.
                  </li>
                )}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="phone" className="flex flex-col gap-2">
            <Input
              placeholder="+34 636 115 993"
              value={phoneInput}
              onChange={(event) => setPhoneInput(event.target.value)}
              onBlur={applyPhoneInput}
            />
            {phonePreview &&
              (phonePreview.ok ? (
                <button
                  type="button"
                  onClick={applyPhoneInput}
                  className="self-start"
                >
                  <Badge
                    variant={
                      pending?.digits === phonePreview.digits
                        ? "default"
                        : "secondary"
                    }
                  >
                    {phonePreview.display} ✓
                  </Badge>
                </button>
              ) : (
                <p className="text-sm text-destructive">{phonePreview.error}</p>
              ))}
          </TabsContent>
        </Tabs>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Scope:</span>
            {ENVIRONMENTS.map((option) => (
              <Button
                key={option.id}
                type="button"
                size="sm"
                variant={environment === option.id ? "default" : "outline"}
                onClick={() => setEnvironment(option.id)}
              >
                {option.label}
              </Button>
            ))}
          </div>
          <Input
            placeholder="Label (optional — e.g. the person's name)"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>

        <div className="flex items-center justify-end gap-2">
          {pending && (
            <span className="mr-auto font-mono text-sm">+{pending.digits}</span>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!pending || isSaving}>
            Add member
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
